/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useEffectEvent, useState } from 'react';
import { requestTimeSeriesDataset } from '@/api/eeCheckApi';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import { parseTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import { normalizeTimeSeriesUrn } from '@/lib/timeSeries/timeSeriesValue';
import type {
    TimeSeriesBandMarker,
    TimeSeriesPlotType,
    TimeSeriesLegendNames,
    TimeSeriesTraceColors,
    TimeSeriesPlotTrafficLightLegendConfig,
    TimeSeriesTimeBand,
    TimeSeriesVerticalMarker,
    TimeSeriesValueBand,
} from '@/lib/timeSeries/timeSeriesPlotly';
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import type { TimeSeriesRequest } from '@/types/eeCheck';
import TimeSeriesPlotFigure from '@/components/charts/TimeSeriesPlotFigure';

interface Api2TimeSeriesPlotProps {
    chartType?: TimeSeriesPlotType;
    transformDataset?: (data: ParsedTimeSeriesCsv) => ParsedTimeSeriesCsv;
    bandMarkers?: TimeSeriesBandMarker[];
    conversion?: number;
    from: string;
    includeZeroYAxis?: boolean;
    legendNames?: TimeSeriesLegendNames;
    minWidth?: string | number;
    onParsedTimeSeriesChange?: (parsedTimeSeries: ParsedTimeSeriesCsv | null) => void;
    revision?: number;
    right?: string[];
    timeBands?: TimeSeriesTimeBand[];
    title?: string;
    trafficLightLegend?: TimeSeriesPlotTrafficLightLegendConfig;
    verticalMarkers?: TimeSeriesVerticalMarker[];
    valueBands?: TimeSeriesValueBand[];
    to: string;
    traceColors?: TimeSeriesTraceColors;
    urn: string | string[];
    xAxisTitle?: string;
    xAxisRange?: [string, string];
    yAxisTitle?: string;
}

function Api2TimeSeriesPlot({
    chartType,
    transformDataset,
    bandMarkers = [],
    conversion = 1,
    from,
    includeZeroYAxis = false,
    legendNames = {},
    minWidth,
    onParsedTimeSeriesChange,
    revision,
    right = [],
    timeBands = [],
    title,
    trafficLightLegend,
    verticalMarkers = [],
    valueBands = [],
    to,
    traceColors = {},
    urn,
    xAxisTitle,
    xAxisRange,
    yAxisTitle,
}: Api2TimeSeriesPlotProps) {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [parsedTimeSeries, setParsedTimeSeries] = useState<ParsedTimeSeriesCsv | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const normalizedUrn = normalizeTimeSeriesUrn(urn);
    const notifyParsedTimeSeriesChange = useEffectEvent(
        (nextParsedTimeSeries: ParsedTimeSeriesCsv | null) => {
            onParsedTimeSeriesChange?.(nextParsedTimeSeries);
        },
    );

    useEffect(() => {
        if (isSessionExpired) {
            notifyParsedTimeSeriesChange(null);
            setIsLoading(false);
            return;
        }

        if (!accessToken || !normalizedUrn || !from || !to) {
            setParsedTimeSeries(null);
            notifyParsedTimeSeriesChange(null);
            setIsLoading(false);
            setErrorMessage(t('Missing time series request parameters.'));
            return;
        }

        const payload: TimeSeriesRequest = {
            from,
            to,
            urn: normalizedUrn,
        };

        let isCancelled = false;

        async function loadFigure(): Promise<void> {
            setIsLoading(true);
            setLoadingLabel(t('Loading API request...'));
            setErrorMessage('');

            try {
                const { data } = await requestTimeSeriesDataset(accessToken, payload, ({ currentRequest, totalRequests }) => {
                    setLoadingLabel(
                        totalRequests > 1
                            ? t('Loading API request {current}/{total}', {
                                current: String(currentRequest),
                                total: String(totalRequests),
                            })
                            : t('Loading API request...'),
                    );
                });

                if (isCancelled) {
                    return;
                }

                if (typeof data !== 'string') {
                    setParsedTimeSeries(null);
                    notifyParsedTimeSeriesChange(null);
                    setErrorMessage(t('Time series response is not a CSV string.'));
                    return;
                }

                const nextParsedTimeSeries = parseTimeSeriesCsv(data);

                setParsedTimeSeries(nextParsedTimeSeries);
                notifyParsedTimeSeriesChange(nextParsedTimeSeries);
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                }

                setParsedTimeSeries(null);
                notifyParsedTimeSeriesChange(null);
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : t('Time series request failed.'),
                );
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                    setLoadingLabel('');
                }
            }
        }

        void loadFigure();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, from, isSessionExpired, normalizedUrn, onSessionExpired, to]);

    return (
        <TimeSeriesPlotFigure
            chartType={chartType}
            bandMarkers={bandMarkers}
            conversion={conversion}
            errorMessage={errorMessage}
            fillParent
            isLoading={isLoading}
            includeZeroYAxis={includeZeroYAxis}
            loadingLabel={loadingLabel}
            legendNames={legendNames}
            minWidth={minWidth}
            parsedTimeSeries={parsedTimeSeries && transformDataset ? transformDataset(parsedTimeSeries) : parsedTimeSeries}
            revision={revision}
            right={right}
            timeBands={timeBands}
            title={title}
            trafficLightLegend={trafficLightLegend}
            verticalMarkers={verticalMarkers}
            valueBands={valueBands}
            traceColors={traceColors}
            xAxisTitle={xAxisTitle}
            xAxisRange={xAxisRange}
            yAxisTitle={yAxisTitle}
        />
    );
}

export default Api2TimeSeriesPlot;
